import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Lock } from 'lucide-react';
import logoIcon from '../assets/logo-icon.png';

const API_BASE = '/api/v1/client-portal';

const ClientResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get('email') || '');
  const [token, setToken] = useState(() => searchParams.get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const passwordsMatch = useMemo(() => newPassword === confirmPassword, [newPassword, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          token: token.trim(),
          newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not reset password.');
      }
      setMessage(data.message || 'Password updated. You can sign in now.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--background)] px-4 py-10">
      <div className="mb-6 flex items-center gap-3">
        <img src={logoIcon} alt="MyMoolah" className="h-10 w-10" />
        <div>
          <p className="text-sm font-semibold text-[var(--foreground)]">MyMoolah Client Portal</p>
          <p className="text-xs text-[var(--muted-foreground)]">Choose a new password</p>
        </div>
      </div>

      <div className="w-full max-w-md rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] p-8 shadow-[var(--portal-shadow)]">
        <div className="mb-2 flex items-center gap-2">
          <Lock className="h-5 w-5 text-[var(--primary)]" aria-hidden />
          <h1 className="text-xl font-semibold text-[var(--foreground)]">Set new password</h1>
        </div>
        <p className="mb-6 text-sm text-[var(--muted-foreground)]">
          Use the reset link from your email, or paste the token below.
        </p>

        {error ? (
          <div className="mb-4 rounded-lg border border-[var(--destructive)]/20 bg-[var(--destructive)]/10 px-4 py-3 text-sm text-[var(--destructive)]" role="alert">
            {error}
          </div>
        ) : null}

        {message ? (
          <div className="mb-4 space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" role="status">
              {message}
            </div>
            <button
              type="button"
              onClick={() => navigate('/client/login', { replace: true })}
              className="min-h-[44px] w-full rounded-[var(--radius)] bg-[var(--primary)] text-sm font-semibold text-[var(--primary-foreground)]"
            >
              Go to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium">Email</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] px-4 py-2.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="token" className="mb-1.5 block text-sm font-medium">Reset token</label>
              <input
                id="token"
                type="text"
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] px-4 py-2.5 text-sm font-mono text-xs"
              />
            </div>
            <div>
              <label htmlFor="new" className="mb-1.5 block text-sm font-medium">New password</label>
              <div className="relative">
                <input
                  id="new"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] py-2.5 pl-4 pr-12 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-0 top-0 flex h-[44px] w-11 items-center justify-center text-[var(--muted-foreground)]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">Min 8 characters, one uppercase letter, one number</p>
            </div>
            <div>
              <label htmlFor="confirm" className="mb-1.5 block text-sm font-medium">Confirm password</label>
              <input
                id="confirm"
                type={showPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="min-h-[44px] w-full rounded-[var(--radius)] border border-[var(--input)] px-4 py-2.5 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="min-h-[44px] w-full rounded-[var(--radius)] bg-[var(--primary)] text-sm font-semibold text-[var(--primary-foreground)] disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Update password'}
            </button>
          </form>
        )}

        <div className="mt-6 flex flex-col items-center gap-2 text-sm">
          <Link to="/client/forgot-password" className="text-[var(--primary)] hover:underline">
            Request a new reset link
          </Link>
          <button type="button" onClick={() => navigate('/client/login')} className="inline-flex items-center gap-1 text-[var(--muted-foreground)] hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientResetPasswordPage;
